import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Users, DollarSign, TrendingUp, LogOut, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [settings, setSettings] = useState({
    platform_name: 'EduFlow',
    primary_color: '#4F46E5',
    secondary_color: '#14B8A6',
    default_course_price: 99.99,
    default_discount: 10,
    max_students: 50,
    stripe_enabled: true,
    razorpay_enabled: true,
    email_notifications: true,
    push_notifications: true,
    stripe_public_key: '',
    razorpay_key_id: '',
    smtp_host: '',
    smtp_port: '',
    smtp_user: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes, coursesRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, getAuthHeader()),
        axios.get(`${API}/users`, getAuthHeader()),
        axios.get(`${API}/courses`, getAuthHeader())
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, getAuthHeader());
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const handleSaveSettings = async (section) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeader());
      toast.success(`${section} settings saved successfully!`);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-extrabold text-slate-900">EduFlow</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
              <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">Admin Dashboard</h1>
          <p className="text-lg text-slate-600">Manage platform and monitor analytics</p>
        </div>

        {/* Tabs for Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="bg-white border border-slate-200 p-1 gap-1">
            <TabsTrigger value="overview" data-testid="overview-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Users</TabsTrigger>
            <TabsTrigger value="courses" data-testid="courses-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Courses</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card data-testid="total-users-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Users</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </Card>

              <Card data-testid="total-courses-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Courses</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.total_courses || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
              </Card>

              <Card data-testid="total-enrollments-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Enrollments</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.total_enrollments || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </Card>

              <Card data-testid="revenue-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Revenue</p>
                    <p className="text-3xl font-bold text-slate-900">₹{stats?.revenue || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card data-testid="users-section" className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">All Users</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{user.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                            user.role === 'teacher' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-teal-100 text-teal-700'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <Card
                  key={course.id}
                  data-testid={`course-card-${course.id}`}
                  className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden card-hover"
                >
                  <div className="h-32 bg-gradient-to-br from-indigo-500 to-teal-500"></div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{course.title}</h3>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{course.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-600">₹{course.price}</span>
                      <span className="text-xs text-slate-500">{course.enrolled_count} students</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Theme Settings */}
              <Card data-testid="theme-settings-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Theme & Branding</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Platform Name</label>
                    <input
                      type="text"
                      data-testid="platform-name-input"
                      value={settings.platform_name}
                      onChange={(e) => updateSetting('platform_name', e.target.value)}
                      className="w-full h-10 px-4 rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Primary Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        data-testid="primary-color-input"
                        value={settings.primary_color}
                        onChange={(e) => updateSetting('primary_color', e.target.value)}
                        className="h-10 w-20 rounded-lg border border-slate-200 cursor-pointer"
                      />
                      <span className="text-sm text-slate-600">{settings.primary_color}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Secondary Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        data-testid="secondary-color-input"
                        value={settings.secondary_color}
                        onChange={(e) => updateSetting('secondary_color', e.target.value)}
                        className="h-10 w-20 rounded-lg border border-slate-200 cursor-pointer"
                      />
                      <span className="text-sm text-slate-600">{settings.secondary_color}</span>
                    </div>
                  </div>
                  <Button 
                    data-testid="save-theme-btn" 
                    onClick={() => handleSaveSettings('Theme')}
                    disabled={savingSettings}
                    className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingSettings ? 'Saving...' : 'Save Theme Settings'}
                  </Button>
                </div>
              </Card>

              {/* Platform Settings */}
              <Card data-testid="platform-settings-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Platform Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Default Course Price (INR)</label>
                    <input
                      type="number"
                      data-testid="default-price-input"
                      value={settings.default_course_price}
                      onChange={(e) => updateSetting('default_course_price', parseFloat(e.target.value))}
                      className="w-full h-10 px-4 rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Default Discount (%)</label>
                    <input
                      type="number"
                      data-testid="default-discount-input"
                      value={settings.default_discount}
                      onChange={(e) => updateSetting('default_discount', parseInt(e.target.value))}
                      min="0"
                      max="100"
                      className="w-full h-10 px-4 rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Max Students per Class</label>
                    <input
                      type="number"
                      data-testid="max-students-input"
                      value={settings.max_students}
                      onChange={(e) => updateSetting('max_students', parseInt(e.target.value))}
                      className="w-full h-10 px-4 rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                    />
                  </div>
                  <Button 
                    data-testid="save-platform-btn" 
                    onClick={() => handleSaveSettings('Platform')}
                    disabled={savingSettings}
                    className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingSettings ? 'Saving...' : 'Save Platform Settings'}
                  </Button>
                </div>
              </Card>
              {/* Payment Settings */}
              <Card data-testid="payment-settings-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Payment Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Stripe</p>
                      <p className="text-xs text-slate-600 mb-2">Credit/Debit cards</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        data-testid="stripe-toggle" 
                        checked={settings.stripe_enabled}
                        onChange={(e) => updateSetting('stripe_enabled', e.target.checked)}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  {settings.stripe_enabled && (
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-1 block">Stripe Public Key</label>
                      <input
                        type="text"
                        data-testid="stripe-key-input"
                        value={settings.stripe_public_key || ''}
                        onChange={(e) => updateSetting('stripe_public_key', e.target.value)}
                        placeholder="pk_test_..."
                        className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                      />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Razorpay</p>
                      <p className="text-xs text-slate-600 mb-2">UPI, Cards, Wallets</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        data-testid="razorpay-toggle" 
                        checked={settings.razorpay_enabled}
                        onChange={(e) => updateSetting('razorpay_enabled', e.target.checked)}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  {settings.razorpay_enabled && (
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-1 block">Razorpay Key ID</label>
                      <input
                        type="text"
                        data-testid="razorpay-key-input"
                        value={settings.razorpay_key_id || ''}
                        onChange={(e) => updateSetting('razorpay_key_id', e.target.value)}
                        placeholder="rzp_test_..."
                        className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                      />
                    </div>
                  )}
                  
                  <Button 
                    data-testid="save-payment-btn" 
                    onClick={() => handleSaveSettings('Payment')}
                    disabled={savingSettings}
                    className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingSettings ? 'Saving...' : 'Save Payment Settings'}
                  </Button>
                </div>
              </Card>

              {/* Notification Settings */}
              <Card data-testid="notification-settings-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Notifications</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Email Notifications</p>
                      <p className="text-xs text-slate-600 mb-2">Send updates via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        data-testid="email-notif-toggle" 
                        checked={settings.email_notifications}
                        onChange={(e) => updateSetting('email_notifications', e.target.checked)}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  {settings.email_notifications && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-slate-700 mb-1 block">SMTP Host</label>
                        <input
                          type="text"
                          data-testid="smtp-host-input"
                          value={settings.smtp_host || ''}
                          onChange={(e) => updateSetting('smtp_host', e.target.value)}
                          placeholder="smtp.gmail.com"
                          className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-700 mb-1 block">Port</label>
                          <input
                            type="text"
                            data-testid="smtp-port-input"
                            value={settings.smtp_port || ''}
                            onChange={(e) => updateSetting('smtp_port', e.target.value)}
                            placeholder="587"
                            className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-700 mb-1 block">User</label>
                          <input
                            type="text"
                            data-testid="smtp-user-input"
                            value={settings.smtp_user || ''}
                            onChange={(e) => updateSetting('smtp_user', e.target.value)}
                            placeholder="email@example.com"
                            className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-slate-900"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Push Notifications</p>
                      <p className="text-xs text-slate-600">Browser notifications</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        data-testid="push-notif-toggle" 
                        checked={settings.push_notifications}
                        onChange={(e) => updateSetting('push_notifications', e.target.checked)}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  
                  <Button 
                    data-testid="save-notification-btn" 
                    onClick={() => handleSaveSettings('Notification')}
                    disabled={savingSettings}
                    className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingSettings ? 'Saving...' : 'Save Notification Settings'}
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
